import { useEffect, useRef } from 'react';

import { PageLayout } from '~/components/unified/PageLayout';

import { PageWrapper } from './PageWrapper';
import { List, ListFooter, ListHeader } from './ProspectList';
import { EmptyStateFilters } from './ProspectList/EmptyStateFilters';
import { useProspector } from './useProspector';
import { hasAnyFilters } from './util';

export function Listing() {
  const [state, send] = useProspector();

  const {
    id,
    prospects,
    deselections,
    totalProspects,
    personQuery,
    companyQuery,
    previousTotalCompanies,
    totalCompanies,
    previousTotalProspects,
    pageEnd,
    pageStart,
    hasNextPage,
    hasPreviousPage,
    loadingPage,
    showFilterSidebar,
  } = state.context;

  const handleSelectAll = () => send('SELECT_ALL');
  const handleSelectAllPage = () => send('SELECT_ALL_PAGE');
  const handleDeselectAll = () => send('DESELECT_ALL');
  const handleSelectItem = (id: string) => send('SELECT_PROSPECT', { id });
  const handleDeselectItem = (id: string) => send('DESELECT_PROSPECT', { id });

  const bodyRef = useRef<HTMLDivElement>(null);

  const selectAllChecked: boolean =
    state.context.selectAll || state.context.selectAllPage;

  const selectedProspects = selectAllChecked
    ? prospects
        .map((p: any) => p.id)
        .filter((id: string) => !deselections.includes(id))
    : state.context.selections;

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [loadingPage, pageEnd, pageStart]);

  const hasItems = prospects.length > 0;
  const hasFilters = hasAnyFilters({ companyQuery, personQuery }) || !!id;
  const isLoading = state.matches('loading');
  const showLoading = loadingPage || isLoading;

  return (
    <PageWrapper>
      <PageLayout.Body ref={bodyRef}>
        <PageLayout.Content className="h-full space-y-4">
          <ListHeader
            hasFilters={hasFilters}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onNextPage={() => send('NEXT_PAGE')}
            onPreviousPage={() => send('PREVIOUS_PAGE')}
            onToggleFilterSidebar={() => send('TOGGLE_FILTER_SIDEBAR')}
            pageEnd={pageEnd}
            pageStart={pageStart}
            previousTotalCompanies={previousTotalCompanies}
            previousTotalProspects={previousTotalProspects}
            showFilterSidebar={showFilterSidebar}
            totalCompanies={totalCompanies}
            totalProspects={totalProspects}
          />
          {hasItems || showLoading ? (
            <List
              isLoading={showLoading}
              onDeselectItem={handleDeselectItem}
              onSelectItem={handleSelectItem}
              prospects={prospects}
              selectedProspects={selectedProspects}
            />
          ) : (
            <EmptyStateFilters className="h-full" />
          )}
        </PageLayout.Content>
      </PageLayout.Body>
      <ListFooter
        onDeselectAll={handleDeselectAll}
        onSelectAll={handleSelectAll}
        onSelectAllPage={handleSelectAllPage}
        selectAllChecked={selectAllChecked}
        selectedProspectsCount={
          state.context.selectAll
            ? state.context.totalProspects - deselections.length
            : state.context.selections.length
        }
        totalProspects={totalProspects}
      />
    </PageWrapper>
  );
}
